import axios, { type AxiosInstance } from 'axios'

// Constants for commonly used values
const CSRF_TOKEN_KEY = 'csrf_token'
const CSRF_HEADER = 'x-csrf-token'

// Helper functions to handle CSRF tokens
const getCsrfToken = () => localStorage.getItem(CSRF_TOKEN_KEY)
const setCsrfToken = (token: string) => localStorage.setItem(CSRF_TOKEN_KEY, token)

const bffInterceptor = (axiosInstance: AxiosInstance): AxiosInstance => {
  const simpleAxios = axios.create({ withCredentials: true })
  const csrfAxios = axios.create({ withCredentials: true })
  let reAuthInProgress = false

  csrfInterceptor(csrfAxios)

  axiosInstance.interceptors.request.use(async (request) => {
    if (reAuthInProgress) {
      const controller = new AbortController()
      controller.abort()
      return { ...request, signal: controller.signal }
    }

    request.headers.set(CSRF_HEADER, getCsrfToken())

    const destinationUrl = axiosInstance.getUri(request)
    const urlObject = new URL(destinationUrl)
    const protocol = urlObject.protocol.slice(0, -1)

    request.url = `${import.meta.env.VITE_BFF_URL}/proxy/${protocol}/${urlObject.host}${urlObject.pathname}${urlObject.pathname.endsWith('/') ? '' : '/'}`
    return request
  })

  axiosInstance.interceptors.response.use(
    (response) => {
      const newToken = response.headers[CSRF_HEADER]
      if (newToken) setCsrfToken(newToken)
      return response
    },
    async (error) => {
      const newToken = error.response?.headers[CSRF_HEADER]
      if (newToken) setCsrfToken(newToken)

      const isAuthError = () =>
        axios.isAxiosError(error) &&
        error.response?.status &&
        [401, 403].includes(error.response.status) &&
        error.response?.data?.startsWith('Proxy Error')

      if (!reAuthInProgress && isAuthError()) {
        reAuthInProgress = true
        try {
          await simpleAxios.post(`${import.meta.env.VITE_BFF_URL}/logout/`)
        } catch (e) {
          console.error("Couldn't logout:", e)
        } finally {
          const res = await csrfAxios.post(`${import.meta.env.VITE_BFF_URL}/bind/`)
          window.location.href = `${import.meta.env.VITE_BFF_URL}/login/`
        }
      }
      return Promise.reject(error)
    },
  )
  return axiosInstance
}

const csrfInterceptor = (axiosInstance: AxiosInstance) => {
  axiosInstance.interceptors.request.use((request) => {
    const newToken = getCsrfToken()
    if (newToken) request.headers.set(CSRF_HEADER, newToken)
    return request
  })

  axiosInstance.interceptors.response.use((response) => {
    const newToken = response.headers[CSRF_HEADER]
    if (newToken) setCsrfToken(newToken)
    return response
  })
}

export { bffInterceptor }
